import { useMemo, useState } from "react";
import {
  Form,
  Space,
  Divider,
  Slider,
  message,
  Typography,
  Row,
  Col,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Card, InputNumber, Select } from "@rumbledirect/ui";

const { Text } = Typography;

interface VariableWeight {
  id: string;
  variable?: string | null;
  value: number;
}

const VARIABLE_OPTIONS = [
  { value: "distance", label: "Afstand tot bestuurder" },
  { value: "nps", label: "NPS" },
  { value: "preferred", label: "Preferred Partner" },
];

export const ClaimRankingConfigCard = () => {
  const [form] = Form.useForm();

  const [variableWeights, setVariableWeights] = useState<VariableWeight[]>([
    {
      id: "w1",
      variable: "distance",
      value: -0.6,
    },
    {
      id: "w3",
      variable: "preferred",
      value: 0.2,
    },
  ]);

  const [networkDistribution, setNetworkDistribution] = useState({
    schadenet: 25,
    abs: 25,
    aas: 25,
    ahg: 25,
  });

  const selectedVariables = useMemo(
    () =>
      variableWeights.map((w) => w.variable).filter((v): v is string => !!v),
    [variableWeights],
  );

  const canAddVariableWeight =
    selectedVariables.length < VARIABLE_OPTIONS.length;

  const addVariableWeight = () => {
    if (!canAddVariableWeight) {
      return message.warning("Alle beschikbare variabelen zijn al toegewezen.");
    }

    setVariableWeights((prev) => [
      ...prev,
      {
        id: Math.random().toString(36).slice(2),
        variable: undefined,
        value: 0,
      },
    ]);
  };

  const updateVariableWeight = (
    id: string,
    updates: Partial<VariableWeight>,
  ) => {
    setVariableWeights((prev) =>
      prev.map((w) => (w.id === id ? { ...w, ...updates } : w)),
    );
  };

  const removeVariableWeight = (id: string) => {
    setVariableWeights((prev) => prev.filter((w) => w.id !== id));
  };

  const updateNetworkValue = (
    key: keyof typeof networkDistribution,
    value: number | null,
  ) => {
    const safe = typeof value === "number" ? value : 0;
    setNetworkDistribution((prev) => ({
      ...prev,
      [key]: safe,
    }));
  };

  const handleSave = () => {
    // Validatie: alle wegingen moeten een type hebben
    const hasEmptyType = variableWeights.some((w) => !w.variable);
    if (hasEmptyType) {
      return message.error(
        "Niet alle variabele wegingen hebben een type. Kies voor elke regel een variabele.",
      );
    }

    message.success("Sturingsconfiguratie opgeslagen");
  };

  const totalNetworkPercentage =
    networkDistribution.schadenet +
    networkDistribution.abs +
    networkDistribution.aas +
    networkDistribution.ahg;

  return (
    <Card title="Sturingsmodule">
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          maxRadius: 50,
        }}
      >
        {/* Variabele wegingen */}
        <Divider orientation="left">Variabele wegingen</Divider>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {variableWeights.map((w) => {
            const availableOptions = VARIABLE_OPTIONS.filter(
              (opt) =>
                opt.value === w.variable ||
                !selectedVariables.includes(opt.value),
            );

            return (
              <Space key={w.id} align="center" style={{ width: "100%" }}>
                <Select
                  placeholder="Kies variabele"
                  style={{ width: 220 }}
                  value={w.variable}
                  options={availableOptions}
                  onChange={(value) =>
                    updateVariableWeight(w.id, { variable: value })
                  }
                />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <Slider
                    min={-1}
                    max={1}
                    step={0.1}
                    value={w.value}
                    onChange={(value) =>
                      updateVariableWeight(w.id, { value: value as number })
                    }
                    dots={false}
                  />
                </div>
                <InputNumber
                  min={-1}
                  max={1}
                  step={0.1}
                  value={w.value}
                  onChange={(value) =>
                    updateVariableWeight(w.id, {
                      value: (value ?? 0) as number,
                    })
                  }
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeVariableWeight(w.id)}
                  danger
                />
              </Space>
            );
          })}

          <Button
            icon={<PlusOutlined />}
            type="dashed"
            onClick={addVariableWeight}
            block
            disabled={!canAddVariableWeight}
          >
            Weging toevoegen
          </Button>
          {!canAddVariableWeight && (
            <Text type="secondary">
              Alle beschikbare variabelen zijn al toegewezen.
            </Text>
          )}
        </Space>

        {/* Afstand */}
        <Divider orientation="left">Afstandsinstellingen</Divider>
        <Form.Item
          label="Maximale afstand (km)"
          name="maxRadius"
          tooltip="Wordt gebruikt voor normalisering in de score."
        >
          <InputNumber min={1} max={200} />
        </Form.Item>

        {/* Herstelnetwerk */}
        <Divider orientation="left">Herstelnetwerk</Divider>

        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Text type={totalNetworkPercentage !== 100 ? "warning" : "secondary"}>
            Totale verdeling: {totalNetworkPercentage}%{" "}
            {totalNetworkPercentage !== 100 &&
              "(advies: stel in dat dit optelt tot 100%)"}
          </Text>

          <Row gutter={16}>
            {/* Schadenet */}
            <Col span={12}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong>Schadenet</Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  <Slider
                    min={0}
                    max={100}
                    value={networkDistribution.schadenet}
                    onChange={(v) => updateNetworkValue("schadenet", v)}
                    style={{ flex: 1 }}
                    dots={false}
                  />
                  <InputNumber
                    min={0}
                    max={100}
                    value={networkDistribution.schadenet}
                    onChange={(v) => updateNetworkValue("schadenet", v)}
                    style={{ width: 80 }}
                  />
                </div>
              </Space>
            </Col>

            {/* ABS */}
            <Col span={12}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong>ABS</Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  <Slider
                    min={0}
                    max={100}
                    value={networkDistribution.abs}
                    onChange={(v) => updateNetworkValue("abs", v)}
                    style={{ flex: 1 }}
                    dots={false}
                  />
                  <InputNumber
                    min={0}
                    max={100}
                    value={networkDistribution.abs}
                    onChange={(v) => updateNetworkValue("abs", v)}
                    style={{ width: 80 }}
                  />
                </div>
              </Space>
            </Col>
          </Row>

          <Row gutter={16}>
            {/* AAS */}
            <Col span={12}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong>AAS</Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  <Slider
                    min={0}
                    max={100}
                    value={networkDistribution.aas}
                    onChange={(v) => updateNetworkValue("aas", v)}
                    style={{ flex: 1 }}
                    dots={false}
                  />
                  <InputNumber
                    min={0}
                    max={100}
                    value={networkDistribution.aas}
                    onChange={(v) => updateNetworkValue("aas", v)}
                    style={{ width: 80 }}
                  />
                </div>
              </Space>
            </Col>

            {/* AHG */}
            <Col span={12}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong>AHG</Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  <Slider
                    min={0}
                    max={100}
                    value={networkDistribution.ahg}
                    onChange={(v) => updateNetworkValue("ahg", v)}
                    style={{ flex: 1 }}
                    dots={false}
                  />
                  <InputNumber
                    min={0}
                    max={100}
                    value={networkDistribution.ahg}
                    onChange={(v) => updateNetworkValue("ahg", v)}
                    style={{ width: 80 }}
                  />
                </div>
              </Space>
            </Col>
          </Row>
        </Space>

        <Divider />

        <Space>
          <Button type="primary" onClick={handleSave}>
            Opslaan
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
